/**
 * An activity's minimum party size (`activities.min_people`).
 *
 * The column existed for years and only the admin and MaJourneys AI read it,
 * so a private day trip priced for two could still be booked by one person on
 * the site. These helpers are the single rule the booking UI and the checkout
 * both apply.
 *
 * Adults and children count toward the minimum. Infants do not: they ride
 * free, so "one adult and a baby" is a party of one as far as the provider's
 * price is concerned.
 */

/** Clean a stored/sent minimum into an integer of at least 1. */
export function normalizeMinPeople(minPeople: number | null | undefined): number {
  const n = Math.floor(Number(minPeople));
  return Number.isFinite(n) && n > 1 ? n : 1;
}

/** Heads that count toward the minimum. */
export function countedTowardMinimum(guests: { adults: number; children: number }): number {
  return Math.max(0, guests.adults) + Math.max(0, guests.children);
}

/** Does this party reach the activity's minimum? */
export function meetsMinPeople(
  minPeople: number | null | undefined,
  guests: { adults: number; children: number },
): boolean {
  return countedTowardMinimum(guests) >= normalizeMinPeople(minPeople);
}

/**
 * The lowest each guest counter may go right now without taking the party
 * below the minimum. Adults never drop under 1; children may reach 0 once the
 * adults alone cover the minimum.
 */
export function guestCounterMins(
  minPeople: number | null | undefined,
  guests: { adults: number; children: number },
): { adults: number; children: number } {
  const min = normalizeMinPeople(minPeople);
  return {
    adults: Math.max(1, min - Math.max(0, guests.children)),
    children: Math.max(0, min - Math.max(0, guests.adults)),
  };
}
